import { ReactNode } from 'react';
import { VisibilityState } from '@tanstack/react-table';
import { Currency, outreach } from '@round/api';
import { Button, ButtonGroup, DropdownMenu, FlagIcon, lucideIcons, Popover, Tooltip } from '@round/creatorbase-ui';
import { asMoneyWithoutZeros } from 'helpers';
import { useCreatorbaseCurrencies } from '@round/creatorbase-helpers/api-hooks';
import AddCreatorToCampaignsDropdown from 'Modules/Plans/CreatorSearch/components/AddCreatorToCampaignsDropdown/AddCreatorToCampaignsDropdown';
import { ADD_TO_CAMPAIGN_PLATFORM, useResultActions } from '../../../../hooks/useResultActions';
import {
    ADD_NEEDS_TAGS_HINT,
    formatCount,
    formatEngagementRate,
    getProfileUrl,
    needsTagsToAdd,
    NO_VALUE,
} from '../../../../helpers';
import styles from './CreatorDetailsPanel.module.css';

const { Archive, ArchiveRestore, Check, ChevronDown, ExternalLink, Megaphone, Plus } = lucideIcons;

const HEADING = 'Creator details';
const ADD_LABEL = 'Add';
const ADDED_LABEL = 'Added';
const ADD_TO_CAMPAIGN_LABEL = 'Add to campaign';
const ARCHIVE_LABEL = 'Archive';
const UNARCHIVE_LABEL = 'Unarchive';
const MORE_LABEL = 'More actions';
const VIEW_PROFILE_LABEL = 'Open on TikTok';
const COLLAPSE_HINT = 'Click the row again to collapse';

type Props = {
    result: outreach.OutreachResult;
    /** Which of the request's optional columns are on, so the panel lists the same stats the table does. */
    columnVisibility: VisibilityState;
};

/** The right-hand side of an expanded result row: its stats, its actions, and a link to its profile. */
export const CreatorDetailsPanel = ({ result, columnVisibility }: Props) => {
    const { getCurrencyById } = useCreatorbaseCurrencies();
    const {
        isAdded,
        isArchived,
        isBusy,
        isUnarchiving,
        handleAdd,
        handleArchive,
        handleUnarchive,
        isCampaignPickerOpen,
        setIsCampaignPickerOpen,
        isOpeningCampaignPicker,
        resolveAccountId,
        showPendingAdd,
    } = useResultActions(result);

    const statsItems = getStatsItems(result, columnVisibility, getCurrencyById);
    // The campaign picker adds the creator first, so it is held back on the same terms as Add.
    const isMissingTags = needsTagsToAdd(result);

    const moreActions = (
        <DropdownMenu>
            <DropdownMenu.Trigger asChild>
                <Button variant="outline" size="xs" aria-label={MORE_LABEL} disabled={isBusy}>
                    <ChevronDown aria-hidden="true" />
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
                align="end"
                // Wide enough for what the items say, rather than for the chevron the menu hangs off.
                className={styles.menu}
                onCloseAutoFocus={(event) => {
                    if (!isOpeningCampaignPicker.current) {
                        return;
                    }

                    isOpeningCampaignPicker.current = false;
                    event.preventDefault();
                }}
            >
                <Tooltip>
                    {/* A disabled item swallows pointer events, so the trigger sits on a wrapper. */}
                    <Tooltip.Trigger asChild>
                        <div>
                            <DropdownMenu.Item
                                disabled={isMissingTags}
                                onSelect={() => {
                                    isOpeningCampaignPicker.current = true;
                                    setIsCampaignPickerOpen(true);
                                }}
                            >
                                <Megaphone aria-hidden="true" />
                                {ADD_TO_CAMPAIGN_LABEL}
                            </DropdownMenu.Item>
                        </div>
                    </Tooltip.Trigger>
                    {isMissingTags && <Tooltip.Content>{ADD_NEEDS_TAGS_HINT}</Tooltip.Content>}
                </Tooltip>
                {!isAdded && (
                    <DropdownMenu.Item onSelect={handleArchive}>
                        <Archive aria-hidden="true" />
                        {ARCHIVE_LABEL}
                    </DropdownMenu.Item>
                )}
            </DropdownMenu.Content>
        </DropdownMenu>
    );

    const primaryAction = isArchived ? (
        <Button
            className={styles.fullWidthButton}
            variant="outline"
            size="xs"
            onClick={handleUnarchive}
            disabled={isUnarchiving}
        >
            <ArchiveRestore aria-hidden="true" />
            {UNARCHIVE_LABEL}
        </Button>
    ) : isAdded ? (
        <div className={styles.addedActions}>
            <span className={styles.addedStatus}>
                <Check aria-hidden="true" />
                {ADDED_LABEL}
            </span>
            {moreActions}
        </div>
    ) : (
        <ButtonGroup className={styles.actions}>
            <Tooltip>
                {/* A disabled button swallows pointer events, so the trigger sits on a wrapper. */}
                <Tooltip.Trigger asChild>
                    <span className={styles.addTrigger}>
                        <Button variant="outline" size="xs" onClick={handleAdd} disabled={isBusy || isMissingTags}>
                            <Plus aria-hidden="true" />
                            {ADD_LABEL}
                        </Button>
                    </span>
                </Tooltip.Trigger>
                {isMissingTags && <Tooltip.Content>{ADD_NEEDS_TAGS_HINT}</Tooltip.Content>}
            </Tooltip>
            {moreActions}
        </ButtonGroup>
    );

    return (
        <section className={styles.container} aria-label={HEADING}>
            <p className={styles.heading}>{HEADING}</p>

            <div className={styles.stats}>
                {statsItems.map((item) => (
                    <div key={item.label} className={styles.statRow}>
                        <span className={styles.statLabel}>{item.label}</span>
                        <span className={styles.statValue}>{item.value}</span>
                    </div>
                ))}
            </div>

            <AddCreatorToCampaignsDropdown
                accountId={result.client_influencer_account_id ?? undefined}
                resolveAccountId={resolveAccountId}
                // No saved account rate here — the result's own rate is passed as `rate` instead.
                clientInfluencer={null}
                rate={{ cost: result.cost, currencyId: result.currency_id }}
                platform={ADD_TO_CAMPAIGN_PLATFORM}
                open={isCampaignPickerOpen}
                onOpenChange={(open) => {
                    setIsCampaignPickerOpen(open);
                    // The add the picker made stands whether or not the post did, so the panel catches
                    // up with it once the picker is done with the space.
                    if (!open) {
                        showPendingAdd();
                    }
                }}
                onPostCreated={showPendingAdd}
                // What opens the picker is the menu item, so the content is positioned against the
                // actions themselves rather than a trigger of its own. Not `asChild`: `primaryAction`
                // can be a `ButtonGroup`, which doesn't forward a ref, so `asChild` would leave the
                // anchor with nothing to measure and the popover would never find a position to open at.
                trigger={<Popover.Anchor>{primaryAction}</Popover.Anchor>}
            />

            <Button className={styles.fullWidthButton} variant="outline" size="xs" asChild>
                <a href={getProfileUrl(result.username)} target="_blank" rel="noreferrer">
                    {VIEW_PROFILE_LABEL}
                    <ExternalLink aria-hidden="true" />
                </a>
            </Button>

            <p className={styles.collapseHint}>{COLLAPSE_HINT}</p>
        </section>
    );
};

const getStatsItems = (
    result: outreach.OutreachResult,
    columnVisibility: VisibilityState,
    getCurrencyById: (id: number | null | undefined) => Currency | undefined
) => {
    const items: { label: string; value: ReactNode }[] = [
        { label: 'Followers', value: formatCount(result.follower_count) },
        { label: 'Engagement rate', value: formatEngagementRate(result.engagement_rate) },
        { label: 'Avg views', value: formatCount(result.avg_views) },
    ];

    if (columnVisibility.location !== false) {
        items.push({
            label: 'Location',
            value: result.location ? (
                <span className={styles.location}>
                    <FlagIcon countryCode={result.location.country_code} />
                    {result.location.country_name}
                </span>
            ) : (
                NO_VALUE
            ),
        });
    }

    if (columnVisibility.email !== false) {
        items.push({ label: 'Email', value: result.email || NO_VALUE });
    }

    if (columnVisibility.rate !== false) {
        items.push({
            label: 'Rate',
            value:
                typeof result.cost === 'number'
                    ? asMoneyWithoutZeros(result.cost, getCurrencyById(result.currency_id))
                    : NO_VALUE,
        });
    }

    return items;
};
